// Pixar Whiteboard Ad's form (live's `pixar-whiteboard-ad-form/library-view`, spec
// G14-pixar-whiteboard-ad PR g2; not captured: no agent has browsed live since 2026-10-22, so built
// from live's code): live's staged form in three approvals. The script stage picks the product and
// writes the script (the server's pack reader and director, or by hand) and approves it; the
// storyboard shows the eight images (`PixarWhiteboardStoryboard.tsx`) and shoots the clips; the
// takes (`PixarWhiteboardTakes.tsx`) are picked under them, and "Generate final video" starts the
// run (`templates/pixar_whiteboard_ad.py`). The draft is the server's (`pixar-whiteboard-draft.ts`).
import { Group, Loader, Stack, Text, Tooltip } from "@mantine/core";
import { Clapperboard, Monitor, Smartphone } from "lucide-react";
import { useState, type ReactNode } from "react";

import { useListTemplateCaptionStyles } from "../../../api/generated/endpoints/generation/generation";
import type { Job } from "../../../api/generated/model";
import {
  VideoTemplateQuote,
  VideoTemplateRequest,
  pixarWhiteboardAdInputsCtaMax,
  pixarWhiteboardAdInputsProblemMax,
  pixarWhiteboardAdInputsPronunciationMax,
  type PixarWhiteboardAdInputsOutput,
} from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import { WhiteboardScriptSection } from "./PixarWhiteboardScript";
import { WhiteboardStoryboard } from "./PixarWhiteboardStoryboard";
import { WhiteboardTakes } from "./PixarWhiteboardTakes";
import { InputChip } from "./PodcastScript";
import { Submit } from "./staged";
import { StreetProductTile, type ProductWords } from "./StreetTiles";
import { Subtitles } from "./Subtitles";
import { ReuseSwitch, type TemplateFormProps } from "./kit";
import {
  STILL_NAMES,
  everyTakePicked,
  usePixarWhiteboardDraft,
  videoBody,
  type CaptionSettings,
  type DraftWork,
} from "./pixar-whiteboard-draft";
import classes from "./podcast.module.css";

/** Live's words (`library-view/constants.ts`). */
const PRODUCT_WORDS: ProductWords = {
  tile:
    "The product and the exact photo of it the ad is built on. Pick one of your products and its " +
    "packshot, or upload a packshot — the pack that lands on the board and the end card shows.",
  name: "The product name the presenter says and the end card shows. Leave empty and it is read off the packshot.",
  text:
    "A description or the product page. Every claim in the script is quoted from it — leave empty " +
    "and the words printed on the pack are used.",
};
const SUBTITLES_TOOLTIP =
  "Burned-in karaoke captions of what the presenter says, over the clips only, never on the end " +
  "card: turn them on or off, pick where they sit and how they look.";
const TAKES_TOOLTIP =
  "How many times each talking clip is shot. Every take is checked word by word; the best one is " +
  "picked for you to confirm.";
const RESOLUTION_TOOLTIP = "Resolution of every clip. 720p costs about 40% of 1080p.";
const FORMAT_TOOLTIP = "Every frame and clip is vertical 9:16.";
const APPROVE_SCRIPT = "Approve script";
const SHOOT_CLIPS = "Shoot the clips";
const GENERATE_VIDEO = "Generate final video";
const PICK_PRODUCT = "Pick a product and its packshot";
const STILLS_PENDING = "Wait for all eight images";
const TAKES_PENDING = "Every clip needs a picked take";
const HANDOFF_NOTICE = "Your Pixar whiteboard ad is finishing in My Generations — you can close this panel now.";
const RESOLUTIONS: readonly PixarWhiteboardAdInputsOutput["resolution"][] = ["1080p", "720p"];
const TAKES = ["1", "2", "3"].map((value) => ({ value, label: value }));
const CHIPS = [
  {
    field: "problem",
    label: "Problem",
    placeholder: "Lips are always dry (optional)",
    tooltip:
      'The everyday problem the hook names, e.g. "lips are always dry". Leave empty and the director ' +
      "takes it from the product text.",
    maxLength: pixarWhiteboardAdInputsProblemMax,
  },
  {
    field: "cta",
    label: "CTA",
    placeholder: "Shop now (optional)",
    tooltip: "The button on the end card, word for word. Leave empty and the director writes a short one.",
    maxLength: pixarWhiteboardAdInputsCtaMax,
  },
  {
    field: "pronunciation",
    label: "Pronunciation",
    placeholder: "GO-lee (optional)",
    tooltip:
      "How your brand name is said, e.g. GO-lee. Leave empty and the director decides what needs a " +
      "respelling.",
    maxLength: pixarWhiteboardAdInputsPronunciationMax,
  },
] as const;

/** Live's progress line under the form (`run-progress.ts`): a director, then the images, then the
 * clips, each take counted once it settles. */
export function progressOf(work: DraftWork, drawn: number, takes: { settled: number; total: number }): string[] {
  if (work.reading) return ["Reading the pack"];
  if (work.writing) return ["Writing the script"];
  return [
    ...(work.boarding ? ["Planning the board"] : []),
    ...(work.drawing ? [`Generating images · ${drawn}/${STILL_NAMES.length}`] : []),
    ...(work.shooting ? [`Generating clips · ${takes.settled}/${takes.total}`] : []),
  ];
}

/**
 * Live's form: "Reuse example" (off and disabled: the card has no example), the stage, and in the
 * footer the subtitles, the takes, the resolution, the 9:16 format and the round start, "Approve
 * script", "Shoot the clips", then "Generate final video", whose tooltip says what it waits for.
 * No cost beside the start, as live's: it approves, then shoots, and only the last click makes the
 * video.
 */
export function PixarWhiteboardAdForm(props: TemplateFormProps) {
  const { teamId, brandId, above, onStarted, example } = props;
  const [collapsed, setCollapsed] = useState(false);
  const flow = usePixarWhiteboardDraft(teamId, brandId);
  const styles = useListTemplateCaptionStyles(teamId).data;
  const [productAsked, setProductAsked] = useState(false);
  const [captions, setCaptions] = useState<CaptionSettings | null>(null);
  // The run started here (live's hand-off notice); a change to the takes ends it.
  const [started, setStarted] = useState<string | null>(null);
  const { form, draft } = flow;
  const approved = draft !== undefined && draft.stage !== "script";
  const shownCaptions: CaptionSettings | null =
    form === null
      ? null
      : ((approved ? captions : null) ?? {
          subtitles: form.inputs.subtitles,
          caption_position: form.inputs.caption_position,
          caption_style_id: form.inputs.caption_style_id,
        });
  const video = draft?.stage !== "takes" || shownCaptions === null ? null : videoBody(draft, shownCaptions);
  const videoRequest = VideoTemplateRequest.safeParse(video);
  const videoQuote = VideoTemplateQuote.safeParse(video);
  const videoStart = usePricedStart({
    teamId,
    brandId,
    quote: videoQuote.success ? videoQuote.data : undefined,
    request: videoRequest.success ? videoRequest.data : undefined,
    onStarted: (job: Job) => {
      setStarted(job.id);
      onStarted(job);
    },
  });
  if (!approved && captions !== null) setCaptions(null);
  if (!approved && started !== null) setStarted(null);

  const panel = (body: ReactNode, footer?: ReactNode) => (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={<ReuseSwitch example={null} checked={false} onChange={() => undefined} />}
      summary={<Text size="sm">Pixar Whiteboard Ad</Text>}
      footer={footer}
    >
      {body}
    </GlassPanel>
  );
  if (flow.read.isError) return panel(<ErrorAlert error={flow.read.error} />);
  if (draft === undefined || form === null || shownCaptions === null) {
    return panel(<Loader size="sm" aria-label="Loading the draft" />);
  }

  const { inputs, script } = form;
  const setInputs = (change: Partial<PixarWhiteboardAdInputsOutput>) => flow.editInputs(change);
  const locked = !flow.editable;
  const writing = flow.work.reading || flow.work.writing;
  const drawn = draft.stills.filter((each) => each.status === "done").length;
  const takes = draft.takes.flatMap((row) => row.takes);
  const settled = takes.filter((take) => take.status !== "working").length;
  const progress = progressOf(flow.work, drawn, { settled, total: takes.length });

  let submit;
  if (draft.stage === "takes") {
    const ready = everyTakePicked(draft);
    submit = (
      <Submit
        label={GENERATE_VIDEO}
        reasons={ready ? [] : [TAKES_PENDING]}
        disabled={!ready || flow.busy || !videoStart.canStart || videoStart.start.isPending || started !== null}
        loading={videoStart.start.isPending}
        onClick={videoStart.startJob}
      />
    );
  } else if (approved) {
    const ready = drawn === STILL_NAMES.length && !flow.work.boarding;
    submit = (
      <Submit
        label={SHOOT_CLIPS}
        reasons={ready ? [] : [STILLS_PENDING]}
        disabled={!ready || flow.busy}
        loading={flow.shooting}
        onClick={flow.shoot}
      />
    );
  } else {
    const reasons = [
      ...(inputs.product ? [] : [PICK_PRODUCT]),
      ...(draft.script_issue === null ? [] : [draft.script_issue]),
    ];
    submit = (
      <Submit
        label={APPROVE_SCRIPT}
        reasons={reasons}
        disabled={reasons.length > 0 || flow.busy}
        loading={flow.approving}
        onClick={flow.approve}
      />
    );
  }

  const footer = (
    <Group justify="space-between" wrap="nowrap" gap={12}>
      <div className={classes.settings}>
        <Subtitles
          value={{
            on: shownCaptions.subtitles,
            position: shownCaptions.caption_position,
            styleId: shownCaptions.caption_style_id || (styles?.default ?? ""),
          }}
          styles={styles?.styles}
          tip={SUBTITLES_TOOLTIP}
          disabled={flow.busy}
          onChange={(value) => {
            const change = {
              subtitles: value.on,
              caption_position: value.position,
              caption_style_id: value.styleId,
            };
            if (approved) setCaptions(change);
            else setInputs(change);
          }}
        />
        <Choice
          setting="Takes"
          icon={<Clapperboard size={16} strokeWidth={1.5} />}
          tip={TAKES_TOOLTIP}
          width={72}
          variant="footer"
          value={String(inputs.takes_per_clip)}
          options={TAKES}
          disabled={locked}
          onChange={(next) => setInputs({ takes_per_clip: Number(next) })}
        />
        <Choice
          setting="Resolution"
          icon={<Monitor size={16} strokeWidth={1.5} />}
          tip={RESOLUTION_TOOLTIP}
          width={88}
          variant="footer"
          value={inputs.resolution}
          options={RESOLUTIONS.map((value) => ({ value, label: value }))}
          disabled={locked}
          onChange={(next) => {
            const resolution = RESOLUTIONS.find((each) => each === next);
            if (resolution !== undefined) setInputs({ resolution });
          }}
        />
        <Tooltip label={FORMAT_TOOLTIP} multiline maw={280} withArrow>
          <span className={classes.setting}>
            <Smartphone size={16} strokeWidth={1.5} aria-hidden />
            9:16
          </span>
        </Tooltip>
      </div>
      {submit}
    </Group>
  );

  const refused = videoStart.start.error ?? videoStart.quoteError ?? flow.failure;
  const status = (
    <>
      {draft.problem === null ? null : (
        <Text className={classes.problem} role="alert">
          {draft.problem}
        </Text>
      )}
      {refused === null ? null : <ErrorAlert error={refused} />}
      {progress.map((line) => (
        <output key={line} className={classes.progress}>
          <Loader size={14} aria-hidden />
          {line}
        </output>
      ))}
      {started === null ? null : (
        <Text component="output" size="sm">
          Generation started. {HANDOFF_NOTICE}
        </Text>
      )}
    </>
  );
  if (approved) {
    return panel(
      <div className={classes.content}>
        <WhiteboardStoryboard
          stills={draft.stills}
          cueWarnings={draft.cue_warnings}
          boarding={flow.work.boarding}
          locked={flow.busy || draft.stage !== "cast"}
          busy={flow.busy}
          resetting={flow.resetting}
          onRegenerate={flow.regenerate}
          onStartOver={flow.startOver}
        />
        {draft.stage === "takes" ? (
          <WhiteboardTakes
            clips={draft.takes}
            busy={flow.busy}
            onPick={(clip, take) => {
              setStarted(null);
              flow.pick(clip, take);
            }}
            onNewTake={(clip) => {
              setStarted(null);
              flow.newTake(clip);
            }}
          />
        ) : null}
        {status}
      </div>,
      footer,
    );
  }

  const body = (
    <div className={classes.content}>
      <Stack gap={12}>
        <div className={classes.tiles}>
          <StreetProductTile
            teamId={teamId}
            brandId={brandId}
            pick={inputs.product ?? null}
            typed={{ product_name: inputs.product_name, product_text: inputs.product_text }}
            disabled={locked}
            missing={productAsked}
            exampleUrls={example?.urls ?? {}}
            words={PRODUCT_WORDS}
            onChange={(product, typed) => setInputs({ product, ...typed, product_category: "" })}
          />
        </div>
        <div className={classes.chips}>
          {CHIPS.map((chip) => (
            <InputChip
              key={chip.field}
              label={chip.label}
              value={inputs[chip.field]}
              placeholder={chip.placeholder}
              tooltip={chip.tooltip}
              maxLength={chip.maxLength}
              disabled={locked}
              onChange={(value) => {
                const change: Partial<PixarWhiteboardAdInputsOutput> = {};
                change[chip.field] = value;
                setInputs(change);
              }}
            />
          ))}
        </div>
        <WhiteboardScriptSection
          script={script}
          timing={draft.timing}
          writing={writing}
          locked={locked}
          issue={script !== null && !writing && flow.edited ? draft.script_issue : null}
          warning={draft.script_warning}
          onGenerate={() => {
            if (!inputs.product) {
              setProductAsked(true);
              return;
            }
            flow.generateScript();
          }}
          onWrite={flow.writeScript}
          onChange={flow.editScript}
        />
      </Stack>
      {status}
    </div>
  );
  return panel(body, footer);
}
